import { formatearNumero } from "@tora/shared";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface PieDeListaProps {
  /** Cuantas filas hay en pantalla. */
  visibles: number;
  /** Cuantas hay en la base para el filtro actual. Lo devuelve toda ruta paginada. */
  total: number;
  /** Que se esta listando, en plural y en minuscula: "ventas", "clientes". */
  sustantivo: string;
  /**
   * Pide mas filas. Si no se pasa, el pie solo informa: es lo que corresponde
   * cuando ya se llego al tope del sistema y la salida es filtrar, no scrollear.
   */
  onVerMas?: (() => void) | undefined;
  /**
   * Que hacer cuando no hay «Ver más», si la salida no es la de siempre
   * (filtrar). Lo usa Catalogo, donde ademas hay que avisar que los contadores
   * de arriba se calculan sobre lo que entro en la ventana.
   */
  detalle?: string;
  cargando?: boolean;
  className?: string;
}

/**
 * Lo que va abajo de una lista que se trajo recortada.
 *
 * Toda ruta paginada devuelve `total` junto a `filas`, asi que la pantalla
 * SIEMPRE sabe cuantas quedaron afuera. Sin este pie, el recorte es invisible:
 * la lista se ve entera, el contador dice otra cosa y nadie se entera hasta que
 * falta algo que existe. Un sistema que recorta en silencio miente.
 *
 * Cuando no queda nada afuera no se dibuja nada: "347 de 347" es ruido.
 *
 * `role="status"` y no `alert`: que el listado tenga mas filas es informacion,
 * no una alarma; se anuncia cuando el lector de pantalla llegue.
 */
export function PieDeLista({
  visibles,
  total,
  sustantivo,
  onVerMas,
  detalle,
  cargando = false,
  className,
}: PieDeListaProps): JSX.Element | null {
  if (total <= visibles) return null;

  const n = (v: number): string => formatearNumero(v, 0);

  return (
    <div
      role="status"
      className={cn(
        "flex flex-col items-center gap-2 py-2 text-center text-xs text-muted-foreground",
        className,
      )}
    >
      <p>
        Se muestran {n(visibles)} de {n(total)} {sustantivo}.
        {onVerMas
          ? null
          : ` ${detalle ?? "Es el máximo que se trae de una: filtrá para ver el resto."}`}
      </p>
      {onVerMas ? (
        <Button type="button" variant="outline" size="sm" onClick={onVerMas} disabled={cargando}>
          Ver más
        </Button>
      ) : null}
    </div>
  );
}
